import {
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  effect,
  ElementRef,
  EventEmitter,
  input,
  Output,
} from "@angular/core";
import { LeafletModule } from "@asymmetrik/ngx-leaflet";
import { LeafletMarkerClusterModule } from "@asymmetrik/ngx-leaflet-markercluster";
import {
  divIcon,
  DivIcon,
  featureGroup,
  FitBoundsOptions,
  LatLng,
  LatLngBounds,
  Map,
  MapOptions,
  Marker,
  marker,
  MarkerCluster,
  MarkerClusterGroup,
  MarkerClusterGroupOptions,
  tileLayer,
} from "leaflet";

export class LatLon {
  public lat: number;
  public lon: number;
  public constructor(lat: number, lon: number) {
    this.lat = lat;
    this.lon = lon;
  }

  public isSame(otherLatOn: LatLon): boolean {
    return this.lat === otherLatOn.lat && this.lon === otherLatOn.lon;
  }

  public toString() {
    return `lat.: ${this.lat}, lon.: ${this.lon}`;
  }

  public serialize(): string {
    return `${this.lat},${this.lon}`;
  }

  public static deserialize(str: string): LatLon {
    const parts = str.split(",");
    if (parts.length === 2) {
      return new LatLon(Number(parts[0]), Number(parts[1]));
    }
    return null;
  }

  public toJsonObject() {
    return {
      latitude: this.lat,
      longitude: this.lon,
    };
  }
}

export interface IBubbleElement {
  id: number;
  size: number;
  coordinates: LatLon;
  label: string;
  style?: string;
  alertCount?: number;
}

const LOG_TAG = "[BUBBLE-MAP] ";

@Component({
  selector: "hydrao-bubble-map",
  templateUrl: "./bubble-map.component.html",
  styleUrls: ["./bubble-map.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LeafletModule, LeafletMarkerClusterModule],
})
export class BubbleMapComponent implements DoCheck {
  public bubbles = input<IBubbleElement[]>([]);
  public selectedBubbleId = input<number>(null);
  public clustering = input<boolean>(true);
  public dispIcon = input<boolean>(true);
  public dispAlertCount = input<boolean>(false);

  @Output()
  public visibleBubblesChange: EventEmitter<IBubbleElement[]> =
    new EventEmitter<IBubbleElement[]>();

  @Output()
  public selectedBubbleIdChange: EventEmitter<number> =
    new EventEmitter<number>();

  protected _bubbles: IBubbleElement[] = [];
  protected _selectedBubbleId: number = null;

  public visibleBubbles: IBubbleElement[];
  public mapOptions: MapOptions;
  public selectedBubble: LatLng = null;
  public map: Map;
  mapFitToBounds: LatLngBounds;

  private _refreshing: boolean = false;
  private _done: boolean = false;
  private _maxSize = 0;
  private _markerGroup: MarkerClusterGroup;

  public markerClusterOptions: MarkerClusterGroupOptions = {
    animateAddingMarkers: true,
    animate: true,
    iconCreateFunction: (cluster: MarkerCluster) => {
      const markers: Marker[] = cluster.getAllChildMarkers();
      let sizes = markers.map((m) => {
        let b = this.bubbles().find((b) => b.id == m.options["id"]);
        return b.size;
      });
      const totalSize = sizes.reduce((a, b) => a + b);
      let icSize = (totalSize / (markers.length * this._maxSize)) * 200; //double size to make it bigger than markers
      if (icSize < 30) icSize = 30;
      return divIcon({
        className: "bubble-cluster",
        html: `<div style="width:${icSize - 10}px; height:${
          icSize - 10
        }px;line-height:${icSize - 10}px; margin: 5px; "><span> ${
          markers.length
        }</span></div>`,
        iconSize: [icSize, icSize],
      });
    },
    polygonOptions: {
      className: "bubble-polygon",
    },
  };
  public markerClusterData: Marker[] = [];

  public mapFitToBoundsOptions: FitBoundsOptions = {
    maxZoom: 12,
    animate: true,
    padding: [20, 20],
  };

  private _hostElement: HTMLElement;
  private _hostWidth: number = 0;
  private _hostHeight: number = 0;

  private _markers: Marker[] = [];

  //https://www.d3-graph-gallery.com/graph/bubblemap_leaflet_basic.html
  //https://esri.github.io/esri-leaflet/examples/styling-clusters.html
  //https://github.com/Leaflet/Leaflet.markercluster

  constructor(private _elRef: ElementRef) {
    this.mapOptions = {
      worldCopyJump: false,
      maxBounds: [
        [-85, -180],
        [85, 180],
      ],
      maxBoundsViscosity: 1.0,
      minZoom: 2,
      layers: [
        tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 18,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }),
      ],
      zoom: 4,
      center: new LatLng(45.18515197288288, 5.7049403680515764),
      //zoom: 5,
      //tap: false,
    };

    effect(() => {
      const bubbles = this.bubbles();

      // update max size from bubbles changes
      if (Array.isArray(bubbles) && bubbles.length > 0) {
        this._maxSize = bubbles
          .map((b) => b.size)
          .reduce((a, b) => (a > b ? a : b));
      } else {
        this._maxSize = 0;
      }

      this._refreshMarkers(this._bubbles, bubbles);
      this._refreshVisibleBubbles();
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
    });

    effect(() => {
      const selectedBubbleId = this.selectedBubbleId();

      // console.log(LOG_TAG + "Apply initial selected from input");
      this._updateSelected(selectedBubbleId);
    });
  }

  public ngDoCheck(): void {
    if (
      this._hostWidth !== this._elRef.nativeElement.offsetWidth ||
      this._hostHeight !== this._elRef.nativeElement.offsetHeight
    ) {
      this._hostWidth = this._elRef.nativeElement.offsetWidth;
      this._hostHeight = this._elRef.nativeElement.offsetHeight;
      console.log(LOG_TAG + "size changed");
      this._sizeChanged();
    }
  }

  public onMapReady(map: Map) {
    // console.log(LOG_TAG + "onMapReady");

    this.map = map;
  }

  public clearSelected() {
    // console.log(LOG_TAG + "clear selected");
    this._selectedBubbleId = null;
    this._refreshDispFromSelectedId();
  }

  public onMarkerClusterReady(markerCluster: MarkerClusterGroup) {
    // console.log(LOG_TAG + "onMarkerClusterReady");
    // Do stuff with group
  }

  public onMoveEnd(map: Map) {
    // console.log(LOG_TAG + "onMoveEnd");
    this._refreshVisibleBubbles();
  }
  public onZoomEnd(map: Map) {
    // console.log(LOG_TAG + "onZoomEnd");
    this._refreshDispFromSelectedId();
    this._refreshVisibleBubbles();
  }

  private _updateSelected(selectedId: number, emitChanges: boolean = false) {
    // if (this._selectedBubbleId !== selectedId) {
    this._selectedBubbleId = selectedId;

    this._refreshDispFromSelectedId();

    if (emitChanges === true) {
      this.selectedBubbleIdChange.emit(selectedId);
    }
    // }
  }

  private _refreshVisibleBubbles() {
    if (!this._refreshing && this.map && this.bubbles()) {
      this.visibleBubbles = [];
      for (let bubble of this.bubbles()) {
        let latLong: LatLng = new LatLng(
          bubble.coordinates.lat,
          bubble.coordinates.lon
        );
        if (this.map.getBounds().contains(latLong)) {
          this.visibleBubbles.push(bubble);
        }
      }

      this.visibleBubblesChange.emit(this.visibleBubbles);
    }
  }

  private _updateIconFromBubble(icon: DivIcon, bubble: IBubbleElement): void {
    if (this._maxSize < 1) this._maxSize = 1; //avoid zero division
    let iconSize = (bubble.size * 100) / this._maxSize;
    if (iconSize < 30) iconSize = 30; //do not allow too small circles
    const fontSize = iconSize < 40 ? Math.round(iconSize - 10) : 30;
    let selected = false;
    if (this._selectedBubbleId == bubble.id) {
      selected = true;
    }

    const classes = ["bubble-marker"];
    if (selected) classes.push("selected");
    if (typeof bubble.style === "string" && bubble.style.trim().length > 0) {
      classes.push(bubble.style);
    }

    icon.options.className = classes.join(" ");
    if (this.dispIcon()) {
      icon.options.html =
        '<div class="mat-icon material-icons mat-icon-no-color" style="vertical-align:super;font-size: ' +
        fontSize +
        "px; width: " +
        fontSize +
        'px;line-height:20px">location_city</div>';
    } else if (this.dispAlertCount() && bubble.alertCount !== undefined) {
      icon.options.html =
        '<div style="font-size: 16px; diplay: flex; color: white; align-items: center; justify-content: center; line-height: 20px;">' +
        bubble.alertCount +
        "</div>";
    } else {
      icon.options.html = "<div></div>";
    }

    icon.options.iconSize = [iconSize, iconSize];
  }

  private _createMarkerFromBubble(bubble: IBubbleElement): Marker {
    //display even unlocalized data
    if (!bubble.coordinates.lat) bubble.coordinates.lat = 0;
    if (!bubble.coordinates.lon) bubble.coordinates.lon = 0;

    let ic: DivIcon = new DivIcon();
    this._updateIconFromBubble(ic, bubble);
    let mark = marker([bubble.coordinates.lat, bubble.coordinates.lon], {
      icon: ic,
    });
    mark.options["size"] = bubble.size;
    mark.options["id"] = bubble.id;
    mark.on("click", (event) => {
      this._updateSelected(bubble.id, true);
    });
    return mark;
  }

  private _updateMarkerFromBubble(bubble: IBubbleElement) {
    let marker = this.markerClusterData.find(
      (m) => m.options["id"] == bubble.id
    );
    if (marker) {
      if (!bubble.coordinates.lat) bubble.coordinates.lat = 0;
      if (!bubble.coordinates.lon) bubble.coordinates.lon = 0;

      this._updateIconFromBubble(marker.getIcon(), bubble);
      //marker.setIcon(this._getIconFromBubble(bubble));
      marker.setLatLng(
        new LatLng(bubble.coordinates.lat, bubble.coordinates.lon)
      );
      return true;
    } else {
      return false;
    }
  }

  private hasChanges(
    oldBubbles: IBubbleElement[],
    newBubbles: IBubbleElement[]
  ): boolean {
    if (!Array.isArray(oldBubbles) || oldBubbles.length !== newBubbles.length)
      return true;

    for (let index = 0; index < newBubbles.length; index++) {
      const newBubble = newBubbles[index];

      const oldBubble = oldBubbles.find((bubble) => bubble.id === newBubble.id);

      if (!oldBubble) return true;

      if (
        !oldBubble.coordinates.isSame(newBubble.coordinates) ||
        oldBubble.label !== newBubble.label ||
        oldBubble.size !== newBubble.size
      ) {
        return true;
      }
    }

    return false;
  }

  private _refreshMarkers(
    oldBubbles: IBubbleElement[],
    newBubbles: IBubbleElement[]
  ) {
    this._refreshing = true;

    if (this.clustering()) {
      let ajustZoom: boolean = false;
      if (!newBubbles || newBubbles.length == 0) {
        this.markerClusterData = [];
      } else if (!oldBubbles || this.hasChanges(oldBubbles, newBubbles)) {
        this.markerClusterData = this.bubbles()
          .map((b) => {
            return this._createMarkerFromBubble(b);
          })
          .filter((m) => m.getLatLng() != null);
        ajustZoom = true;
      } else {
        if (!this.markerClusterData) this.markerClusterData = [];
        for (let bubble of newBubbles) {
          if (!this._updateMarkerFromBubble(bubble)) {
            this.markerClusterData.push(this._createMarkerFromBubble(bubble));
            ajustZoom = true;
          }
        }
      }

      if (ajustZoom) {
        this._markerGroup = new MarkerClusterGroup();

        for (let marker of this.markerClusterData) {
          this._markerGroup.addLayer(marker);
        }

        this.mapFitToBounds = this._markerGroup.getBounds();
        if (!this.mapFitToBounds.isValid()) {
          this.mapFitToBounds = null;
        }
      }
    } else {
      if (this.bubbles() && this.map) {
        this._markers.forEach((m) => this.map.removeLayer(m));

        for (let bubble of this.bubbles()) {
          this._markers.push(
            this._createMarkerFromBubble(bubble).addTo(this.map)
          );
          //this.mapOptions.layers.push(this._createMarkerFromBubble(bubble));
        }

        var group = featureGroup(this._markers);
        this.mapFitToBounds = group.getBounds();
        if (!this.mapFitToBounds.isValid()) {
          this.mapFitToBounds = null;
        }
      }
    }

    this._bubbles = newBubbles;
    this._refreshing = false;
    this._done = true;

    // console.log(
    //   LOG_TAG + "_refreshMarkers END",
    //   "fitToBounds=",
    //   this.mapFitToBounds
    // );
  }

  private _refreshDispFromSelectedId() {
    // console.log(
    //   LOG_TAG + "_refreshDispFromSelectedId : " + this._selectedBubbleId
    // );

    for (let clusterData of this.markerClusterData) {
      let icon: DivIcon = clusterData.getIcon();
      if (clusterData.options["id"] == this._selectedBubbleId) {
        clusterData.setIcon(
          divIcon({
            className: "bubble-marker selected",
            html: icon.options.html,
            iconSize: icon.options.iconSize,
          })
        );
      } else {
        clusterData.setIcon(
          divIcon({
            className: "bubble-marker",
            html: icon.options.html,
            iconSize: icon.options.iconSize,
          })
        );
      }
    }
  }

  private _sizeChanged(): void {
    if (this.map) this.map.invalidateSize(true);
  }
}
